/**
 * @file entries.ts
 * @description Contentstack entry fetching functions with React caching.
 * Provides typed, cached functions for fetching pages, headers, footers, and custom content types.
 */

import contentstack, { Query, QueryOperation, Stack } from '@contentstack/delivery-sdk';
import { cache } from 'react';

import { IAlerts, IFooter, IHeader, ISiteSettings } from '@/.generated';
import { DEFAULT_LOCALE } from '../../constants/locales';
import { GetEntries, GetEntryByUid } from '@/lib/types';
import { createStack } from './delivery-stack';
import { getCurrentLanguage } from './language';
import { addEditableTagsIfPreview, addEditableTagsToEntries } from './preview-helpers';
import { getSiteIdentifier, getSiteTaxonomyField } from './site';
import { maskAssetHosts } from '../../utils/asset-url';

/**
 * Applies taxonomy-based site filter to a Contentstack query when multisite is enabled.
 * No-op when NEXT_PUBLIC_SITE_IDENTIFIER is not configured (single-site mode).
 * Uses a generic to preserve the caller's query type so chained calls (e.g. .find<T>()) remain typed.
 */
function applySiteFilter<Q extends Query>(query: Q): Q {
  const siteId = getSiteIdentifier();
  if (!siteId) return query;
  return query.where(getSiteTaxonomyField(), QueryOperation.EQUALS, siteId) as Q;
}

/** Fields projected onto included page references — just enough to build a link server-side. */
const PAGE_REF_FIELDS = ['url', 'title', 'locale'];

/**
 * Header navigation page-reference paths resolved server-side so links render without a client fetch.
 * Exported so web-component/fetch-header.ts can apply the same projection to its own header query —
 * keep both call sites in sync or the standalone web component falls back to one client-side
 * getEntryWithFields call per nav link.
 */
export const HEADER_NAV_REF_PATHS = [
  'nav_level_one.nav_columns.nav_level_one_links.page_reference',
  'nav_level_one.nav_columns.nav_level_two.navigation_level_two_links.page_reference',
  'nav_level_one.nav_columns.nav_level_two.navigation_level_two_links.navigation_level_three.navigation_level_three_links.page_reference',
  'nav_level_one.nav_columns.nav_level_two.navigation_level_two_links.navigation_level_three.navigation_level_three_links.navigation_level_four.navigation_level_four_links.page_reference',
];

/**
 * Footer navigation and sticky-tile page-reference paths resolved server-side.
 * Exported so web-component/fetch-footer.ts can apply the same projection — see HEADER_NAV_REF_PATHS.
 */
export const FOOTER_NAV_REF_PATHS = [
  'footer_section.navigation_links.page_reference',
  'sticky_tiles_section.account_tile.page_reference',
  'sticky_tiles_section.pay_bill_tile.page_reference',
  'sticky_tiles_section.outages_tile.page_reference',
];

// Builds the only[<path>][] projection params limiting included references to link-building fields.
// The SDK types addParams values as scalars, but its qs serializer supports nested objects.
export const buildRefProjectionParams = (paths: string[]) =>
  ({
    only: Object.fromEntries(paths.map((path) => [path, PAGE_REF_FIELDS])),
  }) as unknown as Record<string, string>;

/**
 * Fetches a page entry by URL with locale support.
 * Includes all referenced content up to 2 levels deep.
 * In multisite mode, results are scoped to the active site via taxonomy filter.
 */
export const getPage = cache(
  async <T>(url: string, pageType: string, locale: string, stackInstance?: Stack) => {
    if (!url || !pageType || !locale) return undefined;

    const stack = stackInstance || createStack();

    try {
      const query = applySiteFilter(
        stack
          .contentType(pageType)
          .entry()
          .locale(locale)
          .query()
          .addParams({ include_all: true, include_dimension: true, include_all_depth: 1 })
          .where('url', QueryOperation.EQUALS, url.toLowerCase())
      );

      const result = maskAssetHosts(await query.find<T & contentstack.Utils.EntryModel>());

      if (result.entries && result.entries.length > 0) {
        const entry = result.entries[0];
        if (entry.locale !== locale) return undefined;
        addEditableTagsIfPreview(entry, pageType, locale);
        return entry;
      }

      return undefined;
    } catch (err) {
      console.error(`Error while fetching page for URL "${url}" (${pageType}, ${locale}):`, err);
      return undefined;
    }
  }
);

/**
 * Fetches header entry for specified locale.
 * When `entryUid` is provided (e.g. live preview of a specific header entry),
 * the same query is filtered to that UID. Keeping the query shape identical to
 * the default path ensures the returned entry has the exact same structure
 * (includeFallback, dimension data, editable-tags `$` object) the Header
 * component expects — fetching via `.entry(uid).fetch()` produces a subtly
 * different shape and breaks SSR rendering.
 */
export const getHeader = cache(async (locale: string, stackInstance?: Stack, entryUid?: string) => {
  if (!locale) return undefined;

  const stack = stackInstance || createStack();

  try {
    let query = applySiteFilter(
      stack
        .contentType('header')
        .entry()
        .includeReference('my_account_login_reference', ...HEADER_NAV_REF_PATHS)
        .locale(locale)
        .includeFallback()
        .query()
        .addParams({ include_dimension: true, ...buildRefProjectionParams(HEADER_NAV_REF_PATHS) })
    );

    if (entryUid) {
      query = query.where('uid', QueryOperation.EQUALS, entryUid) as typeof query;
    }

    const result = maskAssetHosts(await query.find<IHeader>());

    if (result.entries && result.entries.length > 0) {
      const entry = result.entries[0];
      addEditableTagsIfPreview(entry, 'header', locale);
      return entry;
    }

    return undefined;
  } catch (err) {
    console.error(`Error while fetching header for locale "${locale}":`, err);
    return undefined;
  }
});

/**
 * Fetches footer entry for specified locale.
 * When `entryUid` is provided (e.g. live preview of a specific footer entry),
 * the same query is filtered to that UID. Keeping the query shape identical to
 * the default path ensures the returned entry has the exact same structure
 * (includeFallback, dimension data, editable-tags `$` object) the Footer
 * component expects — fetching via `.entry(uid).fetch()` produces a subtly
 * different shape and breaks SSR rendering.
 */
export const getFooter = cache(async (locale: string, stackInstance?: Stack, entryUid?: string) => {
  if (!locale) return undefined;

  const stack = stackInstance || createStack();

  try {
    let query = applySiteFilter(
      stack
        .contentType('footer')
        .entry()
        .includeReference(
          'sticky_tiles_section.log_in_tile.account_login_modal.my_account_login_reference',
          ...FOOTER_NAV_REF_PATHS
        )
        .locale(locale)
        .includeFallback()
        .query()
        .addParams({ include_dimension: true, ...buildRefProjectionParams(FOOTER_NAV_REF_PATHS) })
    );

    if (entryUid) {
      query = query.where('uid', QueryOperation.EQUALS, entryUid) as typeof query;
    }

    const result = maskAssetHosts(await query.find<IFooter>());

    if (result.entries && result.entries.length > 0) {
      const entry = result.entries[0];
      addEditableTagsIfPreview(entry, 'footer', locale);
      return entry;
    }

    return undefined;
  } catch (err) {
    console.error(`Error while fetching footer for locale "${locale}":`, err);
    return undefined;
  }
});

/**
 * Fetches only active alert entries (`active_alert: true`) for the specified locale —
 * filtered server-side so inactive/expired alerts never leave Contentstack, unlike a
 * generic getEntries() call that would return every alert ever created. In multisite
 * mode, results are scoped to the active site via taxonomy filter.
 */
export const getActiveAlerts = cache(
  async (locale: string, stackInstance?: Stack): Promise<IAlerts[]> => {
    if (!locale) return [];

    const stack = stackInstance || createStack();

    try {
      const query = applySiteFilter(
        stack
          .contentType('alerts')
          .entry()
          .locale(locale)
          .includeFallback()
          .query()
          .where('active_alert', QueryOperation.EQUALS, true)
      );

      const result = maskAssetHosts(await query.find<IAlerts & contentstack.Utils.EntryModel>());

      if (result.entries) {
        addEditableTagsToEntries(result.entries, 'alerts');
      }

      return result.entries ?? [];
    } catch (err) {
      console.error(`Error while fetching active alerts (locale: ${locale}):`, err);
      return [];
    }
  }
);

/** Fetches multiple entries of a content type with optional reference includes */
export const getEntries = cache(
  async <T>({
    contentTypeUid,
    referencesToInclude = '',
    locale,
    stackInstance,
    filterBySite = true,
  }: Pick<GetEntries, 'contentTypeUid' | 'referencesToInclude' | 'locale' | 'stackInstance'> & {
    filterBySite?: boolean;
  }) => {
    if (!contentTypeUid) return undefined;

    try {
      const stack = stackInstance || createStack();

      const entryQuery = stack.contentType(contentTypeUid).entry();

      if (referencesToInclude) {
        entryQuery.includeReference(referencesToInclude);
      }

      const localeToUse = locale || getCurrentLanguage();

      // Build the base query, then scope to the active site only when filterBySite is enabled
      let query = entryQuery
        .locale(localeToUse)
        .includeFallback()
        .query()
        .addParams({ include_dimension: true });

      if (filterBySite) query = applySiteFilter(query);

      const entries = maskAssetHosts(await query.find<T & contentstack.Utils.EntryModel>());

      if (entries.entries) {
        addEditableTagsToEntries(entries.entries, contentTypeUid);
      }

      return entries;
    } catch (err) {
      console.error(
        `Error while fetching entries for content type "${contentTypeUid}" (locale: ${locale || getCurrentLanguage()}):`,
        err
      );
      return undefined;
    }
  }
);

/** Fetches all URL slugs for a content type (used for static path generation). Projects only `url`, so no asset host ever appears here — intentionally not passed through maskAssetHosts. */
export const getAllSlugs = cache(
  async <T>({
    contentTypeUid = 'standard_content_page',
    locale,
    stackInstance,
  }: Pick<GetEntries, 'contentTypeUid' | 'stackInstance' | 'locale'>) => {
    if (!contentTypeUid) return undefined;

    try {
      const stack = stackInstance || createStack();

      const localeToUse = locale || getCurrentLanguage();
      const slugs = await applySiteFilter(
        stack
          .contentType(contentTypeUid)
          .entry()
          .locale(localeToUse)
          .includeFallback()
          .only('url')
          .query()
      ).find<T & contentstack.Utils.EntryModel>();

      return slugs;
    } catch (err) {
      console.error(
        `Error while fetching slugs for content type "${contentTypeUid}" (locale: ${locale || getCurrentLanguage()}):`,
        err
      );
      return undefined;
    }
  }
);

/** Fetches site settings (global configuration) */
export const getSiteSettings = cache(
  async (
    contentTypeUid: string = 'site_settings',
    stackInstance?: Stack
  ): Promise<(ISiteSettings & contentstack.Utils.EntryModel) | undefined> => {
    if (!contentTypeUid) return undefined;

    try {
      const stack = stackInstance || createStack();

      const siteSettings = maskAssetHosts(
        await applySiteFilter(
          stack.contentType(contentTypeUid).entry().locale(DEFAULT_LOCALE).query()
        ).find<ISiteSettings & contentstack.Utils.EntryModel>()
      );

      if (siteSettings.entries && siteSettings.entries.length > 0) {
        return siteSettings.entries[0];
      }

      return undefined;
    } catch (err) {
      console.error(
        `Error while fetching site settings for content type "${contentTypeUid}":`,
        err
      );
      return undefined;
    }
  }
);

/**
 * Fetches a projected subset of fields for every entry of a content type using .only().
 * Reduces payload size by returning only the requested fields plus system identifiers
 * (uid, locale). Returns entries actually localized in the requested locale —
 * fallback entries are not included.
 */
export const getEntriesWithFields = cache(
  async <T>(
    contentTypeUid: string,
    fields: string[],
    locale?: string,
    stackInstance?: Stack
  ): Promise<T[]> => {
    if (!contentTypeUid || !fields.length) return [];

    try {
      const stack = stackInstance || createStack();
      const localeToUse = locale || getCurrentLanguage();
      const projection = Array.from(new Set([...fields, 'uid', 'locale']));

      const result = maskAssetHosts(
        await applySiteFilter(
          stack.contentType(contentTypeUid).entry().locale(localeToUse).only(projection).query()
        ).find<T & contentstack.Utils.EntryModel>()
      );

      return (result.entries ?? []).filter((entry) => entry.locale === localeToUse) as T[];
    } catch (err) {
      console.error(
        `Error while fetching fields [${fields.join(', ')}] for content type "${contentTypeUid}" (locale: ${locale || getCurrentLanguage()
        }):`,
        err
      );
      return [];
    }
  }
);

/**
 * Fetches an entry by UID with only the specified fields projected via .only().
 * `T` should describe the shape of the requested fields (e.g. `{ url: string }`).
 * Returns the entry only if it is actually localized in the requested locale —
 * fallback entries are excluded.
 */
export const getEntryWithFields = cache(
  async <T>(
    entryUid: string,
    contentTypeUid: string,
    fields: string[],
    locale?: string,
    stackInstance?: Stack
  ): Promise<T | undefined> => {
    if (!entryUid || !contentTypeUid || !fields.length) return undefined;

    try {
      const stack = stackInstance || createStack();
      const localeToUse = locale || getCurrentLanguage();
      const projection = Array.from(new Set([...fields, 'uid', 'locale']));

      const result = maskAssetHosts(
        await stack
          .contentType(contentTypeUid)
          .entry()
          .locale(localeToUse)
          .only(projection)
          .query()
          .where('uid', QueryOperation.EQUALS, entryUid)
          .find<T & contentstack.Utils.EntryModel>()
      );

      if (result.entries && result.entries.length > 0) {
        if (result.entries[0].locale !== localeToUse) return undefined;
        return result.entries[0] as T;
      }

      return undefined;
    } catch (err) {
      console.error(
        `Error while fetching fields [${fields.join(', ')}] for entry "${entryUid}" in content type "${contentTypeUid}":`,
        err
      );
      return undefined;
    }
  }
);

/**
 * Fetches multiple entries by their UIDs, sorted in the order specified.
 * Useful for maintaining custom ordering of related content.
 */
export const getEntriesByUids = cache(
  async <T>({
    contentTypeUid,
    entryUids,
    referencesToInclude,
    locale,
    stackInstance,
    filterBySite = true,
  }: {
    entryUids?: string | Array<string>;
    filterBySite?: boolean;
  } & Pick<
    GetEntryByUid,
    'contentTypeUid' | 'referencesToInclude' | 'stackInstance' | 'locale'
  >) => {
    if (!entryUids || !contentTypeUid) return undefined;

    try {
      const stack = stackInstance || createStack();

      const entry = stack.contentType(contentTypeUid).entry();

      if (referencesToInclude) {
        entry.includeReference(referencesToInclude);
      }

      const localeToUse = locale || getCurrentLanguage();
      let entryQuery = entry.locale(localeToUse).query();

      // Filter by UIDs first, then optionally scope to the active site
      entryQuery = entryQuery.where('uid', QueryOperation.INCLUDES, entryUids).addParams({
        include_all: true,
        include_all_depth: 1,
      });

      if (filterBySite) entryQuery = applySiteFilter(entryQuery);

      const response = maskAssetHosts(await entryQuery.find<T & contentstack.Utils.EntryModel>());

      if (response.entries && Array.isArray(response.entries) && Array.isArray(entryUids)) {
        if (entryUids.length <= 1 || response.entries.length <= 1) {
          addEditableTagsToEntries(response.entries, contentTypeUid);
          return response;
        }

        // Sort entries to match the order of entryUids
        const entryMap = new Map<string, (typeof response.entries)[number]>();
        for (const entry of response.entries) {
          entryMap.set((entry as any).uid, entry);
        }

        const sortedEntries: typeof response.entries = [];
        for (const uid of entryUids) {
          const entry = entryMap.get(uid);
          if (entry) {
            sortedEntries.push(entry);
          }
        }

        response.entries = sortedEntries;
        addEditableTagsToEntries(response.entries, contentTypeUid);
      }

      return response;
    } catch (err) {
      console.error(
        `Error while fetching entries for UIDs ${Array.isArray(entryUids) ? entryUids.join(', ') : entryUids} in content type "${contentTypeUid}" (locale: ${locale || getCurrentLanguage()}):`,
        err
      );
      return undefined;
    }
  }
);

/** Fetches a single entry by its unique ID */
export const getEntryByUid = cache(
  async <T>({
    stackInstance,
    contentTypeUid,
    entryUid,
    referencesToInclude = '',
    locale,
    filterBySite = true,
  }: { filterBySite?: boolean } & Pick<
    GetEntryByUid,
    'contentTypeUid' | 'entryUid' | 'referencesToInclude' | 'locale' | 'stackInstance'
  >) => {
    if (!entryUid || !contentTypeUid) return undefined;

    try {
      const stack = stackInstance || createStack();

      const entry = stack.contentType(contentTypeUid).entry();

      if (referencesToInclude) {
        entry.includeReference(referencesToInclude);
      }

      const localeToUse = locale || getCurrentLanguage();
      let entryQuery = entry
        .locale(localeToUse)
        .query()
        .where('uid', QueryOperation.EQUALS, entryUid);

      if (filterBySite) entryQuery = applySiteFilter(entryQuery);

      const result = maskAssetHosts(await entryQuery.find<T>());

      if (result.entries && result.entries.length > 0) {
        const fetchedEntry = result.entries[0];
        addEditableTagsIfPreview(fetchedEntry, contentTypeUid, localeToUse);
        return fetchedEntry;
      }

      return undefined;
    } catch (err) {
      console.error(
        `Error while fetching entry "${entryUid}" for content type "${contentTypeUid}" (locale: ${locale || getCurrentLanguage()}):`,
        err
      );
      return undefined;
    }
  }
);
